import {
  Bell,
  CircleUserRound,
  DatabaseBackup,
  HeartPulse,
  Merge,
  Settings,
  ShieldCheck,
  Ticket,
} from "@tamagui/lucide-icons";
import { useGlobalSearchParams, useRouter } from "expo-router";
import { useCallback } from "react";
import { useMedia } from "tamagui";

import {
  Adapt,
  Button,
  CloseButton,
  Dialog,
  Nav,
  ScrollView,
  Sheet,
  View,
  XStack,
  YStack,
} from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { isProd } from "@/config/env";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import DataSharingSettingsScreen from "./tabs/data-sharing";
import EntitlementsSettingsScreen from "./tabs/entitlements";
import GeneralSettings from "./tabs/general";
import InsuranceSettingsScreen from "./tabs/insurance";
import MergeAccountsSettingsScreen from "./tabs/merge-accounts";
import NotificationsSettingsScreen from "./tabs/notifications";
import SettingsIndexScreen from "./tabs/profile";
import ProgramsSettingsScreen from "./tabs/programs";

const SettingsTabs = {
  profile: {
    label: "Profile",
    icon: <CircleUserRound size="$250" flexShrink={0} />,
    component: <SettingsIndexScreen />,
  },
  general: {
    label: "General",
    icon: <Settings size="$250" flexShrink={0} />,
    component: <GeneralSettings />,
  },
  notifications: {
    label: "Notifications",
    icon: <Bell size="$250" flexShrink={0} />,
    component: <NotificationsSettingsScreen />,
  },
  insurance: {
    label: "Insurance",
    icon: <ShieldCheck size="$250" flexShrink={0} />,
    component: <InsuranceSettingsScreen />,
  },
  "data-sharing": {
    label: "Data Sharing",
    icon: <DatabaseBackup size="$250" flexShrink={0} />,
    component: <DataSharingSettingsScreen />,
  },
  "merge-accounts": {
    label: "Merge accounts",
    icon: <Merge size="$250" flexShrink={0} />,
    component: <MergeAccountsSettingsScreen />,
  },
  // Non-production debug surface — gated out of visibleTabs in prod (see below).
  entitlements: {
    label: "Entitlements",
    icon: <Ticket size="$250" flexShrink={0} />,
    component: <EntitlementsSettingsScreen />,
  },
  programs: {
    label: "Programs",
    icon: <HeartPulse size="$250" flexShrink={0} />,
    component: <ProgramsSettingsScreen />,
  },
};
export type SettingsTab = keyof typeof SettingsTabs;
export default function SettingsDialog({ tab }: { tab: SettingsTab }) {
  const router = useRouter();
  const { settings: activeTab } = useGlobalSearchParams<{
    settings: SettingsTab;
  }>();
  const { lg } = useMedia();
  const open = SettingsTabs[tab] ? true : false;
  const { isAuthenticated } = useRequireAuth({ enabled: open });

  const closeDialog = () => router.setParams({ settings: undefined });

  const onClose = (open: boolean) => {
    if (!open) {
      closeDialog();
    }
  };

  // Hides the nav item only. The tab body has its own gate (see
  // `MergeAccountsSettingsScreen`), which is what stops the deep-link from
  // rendering the pane and auto-submitting a merge.
  const { value: isAccountMergeEnabled } = useFeatureFlag(
    "curai-classic-to-chai-account-merge",
    false,
  );

  const visibleTabs = Object.entries(SettingsTabs)
    .filter(([key]) => key !== "entitlements" || !isProd)
    .filter(([key]) => key !== "merge-accounts" || isAccountMergeEnabled)
    .map(([tabKey]) => tabKey as SettingsTab);

  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent, orientation: "horizontal" | "vertical") => {
      const prevKey = orientation === "horizontal" ? "ArrowLeft" : "ArrowUp";
      const nextKey = orientation === "horizontal" ? "ArrowRight" : "ArrowDown";
      if (e.key !== prevKey && e.key !== nextKey) return;

      e.preventDefault();
      const currentIndex = visibleTabs.indexOf(activeTab as SettingsTab);
      if (currentIndex === -1) return;

      const nextIndex =
        e.key === nextKey
          ? (currentIndex + 1) % visibleTabs.length
          : (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
      const nextTab = visibleTabs[nextIndex];
      router.setParams({ settings: nextTab });

      // Move focus to the newly active tab
      const target = e.currentTarget as HTMLElement;
      const buttons = target.querySelectorAll('[role="tab"]');
      (buttons[nextIndex] as HTMLElement)?.focus();
    },
    [activeTab, visibleTabs, router],
  );

  const renderTabButton = (
    tabKey: SettingsTab,
    label: string,
    icon: React.ReactElement,
  ) => (
    <Button
      key={tabKey}
      mode="subMenu"
      icon={icon}
      onPress={() => router.setParams({ settings: tabKey })}
      backgroundColor={activeTab === tabKey ? "$navSelected" : "$transparent"}
      role="tab"
      aria-selected={activeTab === tabKey}
      aria-controls={
        activeTab === tabKey ? `settings-tabpanel-${tabKey}` : undefined
      }
      tabIndex={activeTab === tabKey ? 0 : -1}
    >
      {label}
    </Button>
  );

  const renderAllTabButtons = () =>
    visibleTabs.map((tabKey) => {
      const { label, icon } = SettingsTabs[tabKey];
      return renderTabButton(tabKey, label, icon);
    });

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose} disableRemoveScroll>
      <Dialog.Adapt when="maxLg">
        <Sheet snapPoints={[98]} modal dismissOnOverlayPress disableDrag>
          <Sheet.Frame>
            <Adapt.Contents />
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            backdropFilter="blur(8px)"
          />
        </Sheet>
      </Dialog.Adapt>
      <Dialog.Portal>
        <Dialog.Overlay
          key="overlay"
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
        />
        {/* Set trapped={false} so it does not attempt to steal back focus from Select's FocusScope  */}
        <Dialog.FocusScope key="content" trapped={false} loop focusOnIdle>
          <Dialog.Content
            flexDirection="column"
            $lg={{
              maxWidth: "$constraint",
              width: "100%",
              flexDirection: "row",
              height: "$modalHeight",
              minWidth: 500,
              borderRadius: "$150",
              overflow: "hidden",
            }}
            minWidth="100%"
            padding={0}
          >
            <Nav
              padding="$200"
              flexDirection="row-reverse"
              alignItems="center"
              gap="$100"
              borderBottomWidth="$25"
              borderBottomColor="$fillNeutral"
              $lg={{
                borderBottomWidth: 0,
                minWidth: "$2000",
                padding: "$50",
                paddingTop: "$200",
                flexDirection: "column",
              }}
            >
              {!lg && (
                <Dialog.Close asChild displayWhenAdapted>
                  <CloseButton
                    onClose={closeDialog}
                    marginLeft="auto"
                    alignSelf="center"
                  />
                </Dialog.Close>
              )}

              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                flex={1}
                $lg={{ display: "none" }}
              >
                <XStack
                  role="tablist"
                  aria-orientation="horizontal"
                  gap="$50"
                  paddingHorizontal="$50"
                  onKeyDown={(e: React.KeyboardEvent) =>
                    handleTabKeyDown(e, "horizontal")
                  }
                >
                  {renderAllTabButtons()}
                </XStack>
              </ScrollView>
              <YStack
                role="tablist"
                aria-orientation="vertical"
                flex={1}
                gap="$50"
                display="none"
                $lg={{ display: "flex" }}
                onKeyDown={(e: React.KeyboardEvent) =>
                  handleTabKeyDown(e, "vertical")
                }
              >
                {renderAllTabButtons()}
              </YStack>
            </Nav>
            <View
              role="tabpanel"
              id={activeTab ? `settings-tabpanel-${activeTab}` : undefined}
              aria-label={
                activeTab ? SettingsTabs[activeTab]?.label : undefined
              }
              flex={1}
            >
              {SettingsTabs?.[activeTab]?.component}
            </View>
            <Dialog.Close asChild>
              <CloseButton
                onClose={closeDialog}
                position="absolute"
                top="$screenPadding"
                right="$screenPadding"
                zIndex={1}
              />
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.FocusScope>
      </Dialog.Portal>
    </Dialog>
  );
}
